import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { FaArrowRight, FaBuilding, FaChevronLeft, FaChevronRight, FaDownload, FaEye, FaFileAlt, FaMapMarkerAlt, FaPlus, FaTrash } from "react-icons/fa";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import { getClients } from "../../../../services/global_redux/globale_actions/Client.action";
import { getUnits } from "../../../../services/global_redux/globale_actions/Unit.action";
import { getStates } from "../../../../services/global_redux/globale_actions/States.action";
import { getCurrentMonth } from "../../../../services/global_redux/globale_actions/Currentmonth.action";
import {
  getComplianceDocumentSetup,
  saveComplianceDocumentSetup,
  getComplianceDocuments,
  uploadComplianceDocument,
  deleteComplianceDocument,
  downloadComplianceDocument,
  downloadComplianceDocumentsZip,
  viewComplianceDocument,
  releaseComplianceDocument,
  releaseComplianceDocuments,
  getComplianceDocumentCatalog,
  createComplianceDocumentCatalog,
  deleteComplianceDocumentCatalog,
} from "../../../../services/register_redux/register_actions/ComplianceDocuments.action";
import downloadIcon from "../../../../assets/register_assets/generatedForms/downloadIcon.png";
import bulkDownloadIcon from "../../../../assets/reUsableTemplates_assets/dowload_icon.png";
import "../Generated_Forms/Generated_Forms.css";
import "./ComplianceDocuments.css";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const ALL_MONTHS = "all";

const CATEGORIES = [
  { id: "rl", label: "Registrations & Licences" },
  { id: "cp", label: "Certificates & Permissions" },
  { id: "ch", label: "Statutory Challans" },
];

const DOC_TYPES = {
  rl: [
    { id: "trade_licence", label: "Trade licence" },
    { id: "clra_rc", label: "CLRA RC" },
    { id: "clra_licence", label: "CLRA licence" },
    { id: "ptrc", label: "PTRC" },
    { id: "pt_enroll", label: "PT enrollment" },
    { id: "d365", label: "365 days working" },
  ],
  cp: [
    { id: "night", label: "Night shift permission" },
    { id: "se", label: "S&E certificate" },
    { id: "otherc", label: "Other certificate" },
  ],
  ch: [
    { id: "pf", label: "PF challan" },
    { id: "esic", label: "ESIC challan" },
    { id: "ptc", label: "PT challan" },
    { id: "otherch", label: "Other challan" },
  ],
};

const DOC_LABELS = Object.values(DOC_TYPES).flat().reduce((acc, item) => {
  acc[item.id] = item.label;
  return acc;
}, {});

function hardcodedCatalog() {
  return CATEGORIES.map((cat) => ({
    id: cat.id,
    slug: cat.id,
    label: cat.label,
    types: (DOC_TYPES[cat.id] || []).map((doc) => ({
      id: doc.id,
      slug: doc.id,
      label: doc.label,
      parent_id: cat.id,
    })),
  }));
}

const CATALOG_NEW = "__new__";

const VALIDITY_OPTIONS = [
  { value: "lifetime", label: "Lifetime" },
  { value: "expiry", label: "Expiry" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annually", label: "Semi-annually" },
  { value: "annually", label: "Annually" },
];

const RANGE_VALIDITY = new Set(["quarterly", "semi_annually", "annually"]);

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_FIELDS = 10;
const INPUT_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "state", label: "State" },
  { value: "select", label: "Dropdown" },
  { value: "textarea", label: "Textarea" },
];

function field(key, label, input_type, extra = {}) {
  return {
    key,
    label,
    input_type,
    required: Boolean(extra.required),
    options: extra.options || [],
  };
}

function stripPlaceholders(fields) {
  return (Array.isArray(fields) ? fields : []).map((item) => {
    const next = { ...item };
    delete next.placeholder;
    return next;
  });
}

function defaultCatalog() {
  return {
    trade_licence: [
      field("licence_number", "Licence number", "text", { required: true }),
      field("trade_category", "Trade / business category", "text"),
      field("ward", "Ward / zone", "text"),
      field("state", "State", "state", { required: true }),
      field("issue_date", "Issue date", "date"),
    ],
    clra_rc: [
      field("rc_number", "Registration certificate number", "text", { required: true }),
      field("principal_employer", "Principal employer name", "text"),
      field("max_workmen", "Maximum contract workmen permitted", "number"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    clra_licence: [
      field("licence_number", "Licence number", "text", { required: true }),
      field("contractor_name", "Contractor name", "text"),
      field("workmen_covered", "Number of workmen covered", "number"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    ptrc: [
      field("ptrc_number", "PTRC number", "text", { required: true }),
      field("circle_ward", "Circle / ward", "text"),
      field("return_periodicity", "Return periodicity", "select", { options: ["Monthly", "Annual"] }),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    pt_enroll: [
      field("ptec_number", "PTEC number", "text", { required: true }),
      field("slab_rate", "Applicable slab / rate", "text"),
      field("circle_ward", "Circle / ward", "text"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    d365: [
      field("approval_ref", "Approval reference number", "text", { required: true }),
      field("shift_details", "Shift details", "text"),
      field("approving_authority", "Approving authority", "text"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    night: [
      field("permission_ref", "Permission reference number", "text", { required: true }),
      field("shift_timings", "Shift timings covered", "text"),
      field("women_count", "Number of women employees covered", "number"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    se: [
      field("se_number", "S&E registration number", "text", { required: true }),
      field("establishment_category", "Establishment category", "text"),
      field("employees_count", "Employees at registration", "number"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    otherc: [
      field("certificate_number", "Certificate number", "text", { required: true }),
      field("certificate_type", "Certificate type", "text"),
      field("applicable_act", "Applicable act / rule", "text"),
      field("state", "State", "state"),
      field("issue_date", "Issue date", "date"),
    ],
    pf: [
      field("trrn", "TRRN / challan number", "text", { required: true }),
      field("wage_month", "Wage month", "text"),
      field("amount", "Total amount paid", "number"),
    ],
    esic: [
      field("challan_number", "Challan number", "text", { required: true }),
      field("wage_month", "Wage month", "text"),
      field("amount", "Total amount paid", "number"),
    ],
    ptc: [
      field("cin", "CIN / challan number", "text", { required: true }),
      field("period", "Period covered", "text"),
      field("amount", "Amount paid", "number"),
    ],
    otherch: [
      field("challan_ref", "Challan reference number", "text", { required: true }),
      field("challan_type", "Challan type", "text"),
      field("period", "Period covered", "text"),
    ],
  };
}

function defaultValidity(category) {
  return category === "ch" ? "monthly" : "expiry";
}

function toYearMonth(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
  }
  const s = String(value).trim();
  if (!s || s.startsWith("0000-00-00")) return "";
  const iso = s.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const dmy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
  if (dmy) return `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function todayYearMonth() {
  return toYearMonth(new Date());
}

function minYearMonth(a, b) {
  if (!a) return b || "";
  if (!b) return a;
  return a < b ? a : b;
}

function fallbackStartYm(endYm) {
  const [y, m] = String(endYm || todayYearMonth()).split("-").map(Number);
  if (!y || !m) return endYm || todayYearMonth();
  return `${Math.max(y - 10, 1900)}-${String(m).padStart(2, "0")}`;
}

function unitStartYm(unit) {
  return toYearMonth(
    unit?.Unit_Start_Date || unit?.unit_start_date || unit?.created_at
  );
}

function buildMonthsFromStartToEnd(startYm, endYm) {
  if (!endYm) return [];
  let start = startYm || endYm;
  if (start > endYm) start = endYm;
  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = endYm.split("-").map(Number);
  if (!sy || !sm || !ey || !em) return [endYm];
  const out = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

function slugKey(label) {
  return (
    String(label || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "") || `field_${Date.now()}`
  );
}

function unitLocation(unit) {
  return String(unit?.Location_City || unit?.location_city || "").trim();
}

function branchOptionLabel(unit) {
  const name = String(unit?.unit_name || "").trim();
  const location = unitLocation(unit);
  const id = String(unit?.unit_id || "").trim();
  let place = "";
  if (name && location) place = `${name} (${location})`;
  else place = name || location || "";
  if (id && place) return `${id} - ${place}`;
  return id || place;
}

function orgOptionLabel(client) {
  if (!client) return "";
  const id = String(client.client_id || "").trim();
  const name = String(client.client_name || "").trim();
  if (id && name) return `${id} - ${name}`;
  return id || name;
}

function compactOrgLabel(client, fallbackId) {
  return String(client?.client_id || fallbackId || "").trim();
}

function compactBranchLabel(unit, fallbackId) {
  const id = String(unit?.unit_id || fallbackId || "").trim();
  const location = unitLocation(unit);
  if (id && location) return `${id} (${location})`;
  return id || location;
}

function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function getAssignedClientUnits() {
  try {
    const mapped = JSON.parse(getSessionUser().client_and_unit || "[]");
    return Array.isArray(mapped) ? mapped : [];
  } catch {
    return [];
  }
}

function getAssignedUnits(clientId) {
  const entry = getAssignedClientUnits().find(
    (item) => String(item.client_id) === String(clientId)
  );
  return (entry?.unit_id || []).map(String);
}

function getLockedClientId() {
  const stored = sessionStorage.getItem("selectedClient");
  if (stored && stored !== "null") return stored;
  return getAssignedClientUnits()[0]?.client_id || "";
}

function formatBranchPlace(unit) {
  const state = String(unit?.state || unit?.state_name || "").trim();
  const location = unitLocation(unit);
  if (state && location) return `${state}, ${location}`;
  return state || location || "";
}

function monthsFromUploadRows(rows) {
  return [...new Set(
    (Array.isArray(rows) ? rows : []).map((row) => rowYearMonth(row)).filter(Boolean)
  )].sort();
}

function monthLabel(mm, yy) {
  const name = MONTH_NAMES[parseInt(mm, 10) - 1];
  return name ? `${name} ${yy}` : `${mm} ${yy}`;
}

function matchesInlinePeriod(row, inlineList) {
  if (!inlineList) return false;
  const ym = rowYearMonth(row);
  if (inlineList.allMonths) {
    return String(ym).startsWith(`${inlineList.year}-`);
  }
  return ym === inlineList.date;
}

function periodHeading(inlineList) {
  if (!inlineList) return "";
  if (inlineList.allMonths) {
    return inlineList.year ? `All months ${inlineList.year}` : "All months";
  }
  if (inlineList.date && inlineList.date.length >= 7) {
    return monthLabel(inlineList.date.slice(5, 7), inlineList.date.slice(0, 4));
  }
  return "";
}

function formatDateDdMmYyyy(value) {
  if (!value) return "";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function rowYearMonth(row) {
  if (row?.year && row?.month) return `${row.year}-${row.month}`;
  return toYearMonth(row?.period_start);
}

function errText(value, fallback) {
  if (typeof value === "string" && value.trim()) return value;
  return fallback;
}

function clientDownloadErrorMessage(message, fallback = "Download failed.") {
  const text = String(message || "").toLowerCase();
  if (text.includes("file not found") || text.includes("no file found")) {
    return "This document isn’t available to download right now.";
  }
  return errText(message, fallback);
}

function fileLooksAllowed(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const extOk = ALLOWED_EXT.some((ext) => name.endsWith(ext));
  const typeOk = !file.type || ALLOWED_TYPES.includes(file.type);
  return extOk && typeOk;
}

function mapUploadRow(row, orgLabel, branchLabel) {
  const start = String(row.period_start || "");
  return {
    id: row.id,
    doc: row.document_id,
    document_name: row.document_name,
    fileName: row.file_name,
    client_id: row.client_id,
    client_label: orgLabel,
    unit_id: row.unit_id,
    unit_label: branchLabel,
    year: start.slice(0, 4),
    month: start.slice(5, 7),
    period_start: row.period_start,
    expiry_date: row.expiry_date,
    validity_type: row.validity_type,
    is_released: row.is_released === true || row.is_released === 1 || row.is_released === "1",
    answers: row.answers || {},
    snapshot: row.fields_json || [],
  };
}

function periodDisplay(row) {
  if (RANGE_VALIDITY.has(row.validity_type) && row.period_start && row.expiry_date) {
    return `${formatDateDdMmYyyy(row.period_start)} – ${formatDateDdMmYyyy(row.expiry_date)}`;
  }
  if (row.month && row.year) return monthLabel(row.month, row.year);
  return formatDateDdMmYyyy(row.period_start) || "—";
}

function FieldInput({ fieldDef, value, onChange, states }) {
  const common = {
    id: `cd-ans-${fieldDef.key}`,
    value: value || "",
    onChange: (e) => onChange(fieldDef.key, e.target.value),
  };

  if (fieldDef.input_type === "textarea") {
    return <textarea {...common} />;
  }
  if (fieldDef.input_type === "state") {
    return (
      <select {...common}>
        <option value="">Select</option>
        {states.map((item) => (
          <option key={item.state_name} value={item.state_name}>
            {item.state_name}
          </option>
        ))}
      </select>
    );
  }
  if (fieldDef.input_type === "select") {
    return (
      <select {...common}>
        <option value="">Select</option>
        {(fieldDef.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      {...common}
      type={fieldDef.input_type === "date" ? "date" : fieldDef.input_type === "number" ? "number" : "text"}
    />
  );
}

function ComplianceDocuments({ setLoading = () => {}, showSnackbar = () => {} }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const monthLoadId = useRef(0);
  const stickyRef = useRef(null);
  const catTabsRef = useRef(null);
  const sessionUser = getSessionUser();
  const isClient = sessionUser?.base_role === "Client";
  const lockedClientId = isClient ? getLockedClientId() : "";

  const [clientOptions, setClientOptions] = useState([]);
  const [clientID, setClientID] = useState(isClient ? lockedClientId : "");
  const [unitOptions, setUnitOptions] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [allowedMonths, setAllowedMonths] = useState([]);
  const [activeCat, setActiveCat] = useState("rl");
  const [currentDoc, setCurrentDoc] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [catalogDialog, setCatalogDialog] = useState(false);
  const [catalogParentSlug, setCatalogParentSlug] = useState("");
  const [catalogCategoryName, setCatalogCategoryName] = useState("");
  const [catalogTypeName, setCatalogTypeName] = useState("");
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogDelete, setCatalogDelete] = useState(null);
  const [catalogDeleting, setCatalogDeleting] = useState(false);
  const [catalogChecked, setCatalogChecked] = useState({});
  const [canScrollCatsLeft, setCanScrollCatsLeft] = useState(false);
  const [canScrollCatsRight, setCanScrollCatsRight] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);
  const [records, setRecords] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [liveFields, setLiveFields] = useState([]);
  const [validityType, setValidityType] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [answers, setAnswers] = useState({});
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const [previewType, setPreviewType] = useState("");
  const [imageZoom, setImageZoom] = useState(1);
  const [fitSize, setFitSize] = useState({ w: 0, h: 0 });
  const imageZoomRef = useRef(1);
  const imageScrollerRef = useRef(null);
  const fitSizeRef = useRef({ w: 0, h: 0 });
  const pinchRef = useRef({ startDist: 0, startZoom: 1 });
  const dragRef = useRef({ on: false, x: 0, y: 0, sl: 0, st: 0 });
  const previewUrlRef = useRef("");
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("text");
  const [newOptions, setNewOptions] = useState("");
  const [newRequired, setNewRequired] = useState("0");
  const [uploading, setUploading] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseAllRows, setReleaseAllRows] = useState([]);
  const [releaseYear, setReleaseYear] = useState("");
  const [releaseMonth, setReleaseMonth] = useState("");
  const [releaseChecked, setReleaseChecked] = useState({});
  const [releasing, setReleasing] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [inlineList, setInlineList] = useState(null);
  const [clientDocs, setClientDocs] = useState([]);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [busyDocId, setBusyDocId] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);

  const categories = catalog.length ? catalog : hardcodedCatalog();
  const activeCategory = categories.find((cat) => cat.slug === activeCat || cat.code === activeCat) || categories[0] || null;
  const typeList = activeCategory?.types || [];
  const catalogSelectedCat = categories.find((cat) => cat.slug === catalogParentSlug || cat.code === catalogParentSlug) || null;
  const catalogSelectedTypes = catalogSelectedCat?.types || [];
  const docLabels = useMemo(() => {
    const acc = { ...DOC_LABELS };
    categories.forEach((cat) => {
      (cat.types || []).forEach((doc) => {
        if (doc?.slug || doc?.code) acc[doc.slug || doc.code] = doc.label || doc.name;
      });
    });
    return acc;
  }, [categories]);

  const selectedClient = clientOptions.find(
    (c) => String(c.client_id) === String(clientID)
  );
  const selectedBranch = unitOptions.find(
    (u) => String(u.unit_id) === String(selectedUnit)
  );
  const orgLabel = orgOptionLabel(selectedClient) || clientID;
  const branchLabel = branchOptionLabel(selectedBranch) || selectedUnit;
  const hasOrgBranch = Boolean(clientID && selectedUnit);
  const periodReady = Boolean(hasOrgBranch && selectedYear && selectedMonth);
  const canUpload = Boolean(periodReady && currentDoc);
  const hasValidity = Boolean(validityType);
  const isRange = RANGE_VALIDITY.has(validityType);
  const yearOptions = useMemo(
    () => [...new Set(allowedMonths.map((ym) => ym.slice(0, 4)))].sort((a, b) => Number(b) - Number(a)),
    [allowedMonths]
  );
  const monthOptions = useMemo(
    () => allowedMonths.filter((ym) => ym.startsWith(`${selectedYear}-`)).map((ym) => ym.slice(5, 7)),
    [allowedMonths, selectedYear]
  );
  const releaseMonthOptions = useMemo(
    () => allowedMonths.filter((ym) => ym.startsWith(`${releaseYear}-`)).map((ym) => ym.slice(5, 7)),
    [allowedMonths, releaseYear]
  );

  const uploadedMonths = useMemo(() => {
    const seen = new Set();
    const out = [];
    records.forEach((row) => {
      const ym = rowYearMonth(row);
      if (!ym || seen.has(ym)) return;
      seen.add(ym);
      out.push(ym);
    });
    return out.sort();
  }, [records]);

  const tableRows = useMemo(() => {
    if (!canUpload) return [];
    return records.filter((row) => {
      if (filterMonth && rowYearMonth(row) !== filterMonth) return false;
      return true;
    });
  }, [records, canUpload, filterMonth]);

  const releaseMonthRows = useMemo(() => {
    if (!releaseYear || !releaseMonth) return [];
    return releaseAllRows.filter(
      (row) => row.year === releaseYear && row.month === releaseMonth
    );
  }, [releaseAllRows, releaseYear, releaseMonth]);

  const releaseSelectedRows = useMemo(
    () => releaseMonthRows.filter((row) => releaseChecked[row.id]),
    [releaseMonthRows, releaseChecked]
  );

  const releaseSelectedIds = useMemo(
    () => releaseSelectedRows.map((row) => row.id),
    [releaseSelectedRows]
  );

  const releaseHeaderLabel =
    releaseSelectedRows.length > 0 && releaseSelectedRows.every((row) => row.is_released)
      ? "Re-release"
      : "Release";

  const releaseAllSelected =
    releaseMonthRows.length > 0 &&
    releaseMonthRows.every((row) => releaseChecked[row.id]);

  useEffect(() => {
    if (filterMonth && !uploadedMonths.includes(filterMonth)) {
      setFilterMonth("");
    }
  }, [filterMonth, uploadedMonths]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clientsRes, statesRes, catalogRes] = await Promise.all([
          dispatch(getClients()),
          isClient ? Promise.resolve({ data: { states: [] } }) : dispatch(getStates()),
          dispatch(getComplianceDocumentCatalog()),
        ]);
        if (cancelled) return;
        if (catalogRes?.success && Array.isArray(catalogRes.data) && catalogRes.data.length) {
          setCatalog(catalogRes.data);
          const firstSlug = catalogRes.data[0]?.slug;
          if (firstSlug) {
            setActiveCat((prev) => (
              catalogRes.data.some((cat) => cat.slug === prev) ? prev : firstSlug
            ));
          }
        }
        if (clientsRes.success && Array.isArray(clientsRes.data)) {
          setClientOptions(clientsRes.data);
        } else {
          setClientOptions([]);
          showSnackbar(clientsRes.message || "Could not load organizations.", "error");
        }
        const states = statesRes?.data?.states;
        setStateOptions(Array.isArray(states) ? states : []);

        if (isClient && lockedClientId) {
          setClientID(lockedClientId);
          sessionStorage.setItem("selectedClient", lockedClientId);
          const assigned = getAssignedUnits(lockedClientId);
          const response = await dispatch(getUnits(lockedClientId));
          if (cancelled) return;
          const rows = Array.isArray(response.data) ? response.data : [];
          setUnitOptions(rows.filter((u) => {
            if (String(u.client_id) !== String(lockedClientId)) return false;
            if (!assigned.length) return true;
            return assigned.includes(String(u.unit_id));
          }));
        }
      } catch {
        if (!cancelled) {
          setClientOptions([]);
          showSnackbar("Could not load organizations.", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isClient, lockedClientId, setLoading, showSnackbar]);

  useEffect(() => {
    const locked = fieldsOpen || Boolean(viewRow) || releaseOpen;
    const main = document.querySelector(".mainbody");
    if (!locked || !main) return undefined;
    const top = main.scrollTop;
    const prevOverflow = main.style.overflow;
    const prevOverflowY = main.style.overflowY;
    main.style.overflow = "hidden";
    main.style.overflowY = "hidden";
    main.scrollTop = top;
    return () => {
      main.style.overflow = prevOverflow;
      main.style.overflowY = prevOverflowY;
      main.scrollTop = top;
    };
  }, [fieldsOpen, viewRow, releaseOpen]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const stopPageScroll = (e) => {
    const scroller = e.currentTarget.querySelector(".cd-editor-scroll, .cd-modal, .cd-modal-release, .cd-image-scroller");
    if (scroller && scroller.contains(e.target)) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const resetPeriod = () => {
    setSelectedYear("");
    setSelectedMonth("");
    setPickedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetDocInputs = () => {
    setAnswers({});
    setValidityType("");
    setValidUntil("");
    setFromDate("");
    setToDate("");
    setPickedFile(null);
    setFieldsOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const persistSetup = async (fields, validity) => {
    if (isClient || !currentDoc) return { success: false };
    const result = await dispatch(saveComplianceDocumentSetup({
      category: activeCat,
      document_id: currentDoc,
      document_name: docLabels[currentDoc],
      validity_type: validity || defaultValidity(activeCat),
      fields_json: stripPlaceholders(fields),
    }));
    if (!result.success) {
      showSnackbar(errText(result.message, "Could not save fields."), "error");
    }
    return result;
  };

  const closeCatalogDialog = () => {
    setCatalogDialog(false);
    setCatalogParentSlug("");
    setCatalogCategoryName("");
    setCatalogTypeName("");
    setCatalogSaving(false);
    setCatalogChecked({});
  };

  const updateCatScroll = () => {
    const el = catTabsRef.current;
    if (!el) {
      setCanScrollCatsLeft(false);
      setCanScrollCatsRight(false);
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    const overflow = max > 4;
    setCanScrollCatsLeft(overflow && el.scrollLeft > 4);
    setCanScrollCatsRight(overflow && el.scrollLeft < max - 4);
  };

  const scrollCats = (dir) => {
    catTabsRef.current?.scrollBy({ left: dir * 180, behavior: "smooth" });
  };

  useLayoutEffect(() => {
    updateCatScroll();
    const el = catTabsRef.current;
    if (!el) return undefined;
    const active = el.querySelector(".cd-cat-tab.active");
    active?.scrollIntoView({ inline: "nearest", block: "nearest" });
    const observer = new ResizeObserver(updateCatScroll);
    observer.observe(el);
    window.addEventListener("resize", updateCatScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateCatScroll);
    };
  }, [activeCat, catalog, categories.length]);

  const openCatalogDialog = () => {
    setCatalogParentSlug(activeCat || CATALOG_NEW);
    setCatalogCategoryName("");
    setCatalogTypeName("");
    setCatalogChecked({});
    setCatalogDialog(true);
  };

  const handleCatalogCreate = async () => {
    const typeLabel = catalogTypeName.trim();
    const isNewCategory = catalogParentSlug === CATALOG_NEW;
    const categoryLabel = catalogCategoryName.trim();
    if (isNewCategory && !categoryLabel) {
      showSnackbar("Category name is required.", "warning");
      return;
    }
    if (!typeLabel) {
      showSnackbar("Document type is required.", "warning");
      return;
    }
    setCatalogSaving(true);
    let parentId;
    let parentSlug = catalogParentSlug;
    if (isNewCategory) {
      const parentRes = await dispatch(createComplianceDocumentCatalog({ label: categoryLabel, name: categoryLabel }));
      if (!parentRes.success) {
        setCatalogSaving(false);
        showSnackbar(errText(parentRes.message, "Could not create category."), "error");
        return;
      }
      if (Array.isArray(parentRes.categories) && parentRes.categories.length) {
        setCatalog(parentRes.categories);
      }
      parentId = Number(parentRes.data?.id);
      parentSlug = parentRes.data?.slug || parentRes.data?.code || parentSlug;
    } else {
      const selected = categories.find((cat) => cat.slug === catalogParentSlug || cat.code === catalogParentSlug);
      parentId = Number(selected?.id);
      parentSlug = selected?.slug || selected?.code || catalogParentSlug;
    }

    const typePayload = { label: typeLabel, name: typeLabel };
    if (Number.isInteger(parentId) && parentId > 0) {
      typePayload.parent_id = parentId;
      typePayload.category_id = parentId;
    }
    if (parentSlug && parentSlug !== CATALOG_NEW) {
      typePayload.parent_slug = parentSlug;
      typePayload.parent_code = parentSlug;
    }

    const typeRes = await dispatch(createComplianceDocumentCatalog(typePayload));
    setCatalogSaving(false);
    if (!typeRes.success) {
      showSnackbar(errText(typeRes.message, "Could not create document type."), "error");
      if (isNewCategory && parentSlug) setActiveCat(parentSlug);
      return;
    }
    if (Array.isArray(typeRes.categories) && typeRes.categories.length) {
      setCatalog(typeRes.categories);
    }
    const createdTypeSlug = typeRes.data?.slug || typeRes.data?.code || "";
    closeCatalogDialog();
    showSnackbar(typeRes.message || "Created successfully.", "success");
    if (parentSlug) setActiveCat(parentSlug);
    if (createdTypeSlug) {
      resetDocInputs();
      setCurrentDoc(createdTypeSlug);
      const setup = await dispatch(getComplianceDocumentSetup({
        category: parentSlug,
        document_id: createdTypeSlug,
        document_name: typeRes.data?.label || typeRes.data?.name || typeLabel,
      }));
      if (setup.success && setup.data) {
        setLiveFields(stripPlaceholders(setup.data.fields_json));
      } else {
        setLiveFields([]);
      }
    }
  };

  const catalogItemId = (item) => {
    const id = Number(item?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
  };

  const catalogDeleteItems = (items) => {
    const list = (items || []).filter((item) => catalogItemId(item));
    if (!list.length) {
      showSnackbar("Select at least one item to delete.", "warning");
      return;
    }
    setCatalogDelete({
      items: list.map((item) => ({
        id: catalogItemId(item),
        kind: item.kind,
        name: item.label || item.name || (item.kind === "category" ? "category" : "document type"),
        slug: item.slug || item.code || "",
      })),
    });
  };

  const closeCatalogDelete = () => {
    if (catalogDeleting) return;
    setCatalogDelete(null);
  };

  const handleCatalogDelete = async () => {
    const items = Array.isArray(catalogDelete?.items) ? catalogDelete.items : [];
    if (!items.length) return;
    const types = items.filter((item) => item.kind === "type");
    const cats = items.filter((item) => item.kind === "category");
    setCatalogDeleting(true);
    let nextCatalog = catalog;
    let failed = "";
    const runDelete = async (item) => {
      const result = await dispatch(deleteComplianceDocumentCatalog(item.id));
      if (!result.success) {
        failed = errText(result.message, `Could not delete ${item.name}.`);
        return false;
      }
      if (Array.isArray(result.categories) && result.categories.length) {
        nextCatalog = result.categories;
      }
      return true;
    };
    for (const item of types) {
      if (!(await runDelete(item))) break;
    }
    if (!failed) {
      for (const item of cats) {
        if (!(await runDelete(item))) break;
      }
    }
    setCatalogDeleting(false);
    if (nextCatalog.length) setCatalog(nextCatalog);
    setCatalogChecked({});
    const parent = nextCatalog.find((cat) => cat.slug === activeCat || cat.code === activeCat) || nextCatalog[0];
    if (cats.length) {
      const nextCat = nextCatalog[0];
      const nextSlug = nextCat?.slug || nextCat?.code || CATALOG_NEW;
      setActiveCat(nextSlug === CATALOG_NEW ? "" : nextSlug);
      setCatalogParentSlug(nextSlug);
      setCurrentDoc("");
      resetDocInputs();
    } else {
      const stillSelected = (parent?.types || []).some(
        (doc) => doc.slug === currentDoc || doc.code === currentDoc
      );
      if (!stillSelected) {
        setCurrentDoc("");
        resetDocInputs();
      }
    }
    setCatalogDelete(null);
    if (failed) {
      showSnackbar(failed, "error");
      return;
    }
    showSnackbar(
      items.length > 1 ? "Selected items deleted." : "Deleted successfully.",
      "success"
    );
  };

  const loadSetup = async (category, documentId) => {
    const result = await dispatch(getComplianceDocumentSetup({
      category,
      document_id: documentId,
      document_name: docLabels[documentId],
    }));
    if (result.success && result.data) {
      setLiveFields(stripPlaceholders(result.data.fields_json));
      return;
    }
    setLiveFields(defaultCatalog()[documentId] || []);
    if (result.message) {
      showSnackbar(errText(result.message, "Could not load fields."), "error");
    }
  };

  const applyMonthRange = (startYm, endYm) => {
    const end = endYm || todayYearMonth();
    const start = startYm || fallbackStartYm(end);
    const months = buildMonthsFromStartToEnd(start, end);
    setAllowedMonths(months);
    return months;
  };

  const loadAllowedMonths = async (clientId, unit) => {
    const req = ++monthLoadId.current;
    if (isClient) {
      const unitId = unit?.unit_id || unit;
      try {
        const result = await dispatch(getComplianceDocuments({
          client_id: clientId,
          unit_id: unitId,
        }));
        if (req !== monthLoadId.current) return;
        if (!result.success) {
          setAllowedMonths([]);
          showSnackbar(errText(result.message, "Could not load documents."), "error");
          return;
        }
        const mapped = (result.data || []).map((row) => mapUploadRow(row, "", ""));
        const months = monthsFromUploadRows(mapped);
        setAllowedMonths(months);
        if (!months.length) {
          showSnackbar("No documents found for this branch.", "warning");
        }
      } catch {
        if (req !== monthLoadId.current) return;
        setAllowedMonths([]);
        showSnackbar("Could not load documents.", "error");
      }
      return;
    }
    const todayYm = todayYearMonth();
    const startYm = unitStartYm(unit);
    applyMonthRange(startYm, todayYm);
    try {
      sessionStorage.setItem("selectedClient", clientId);
      const response = await dispatch(getCurrentMonth(clientId));
      if (req !== monthLoadId.current) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      const match = rows.find((row) => String(row.client_id) === String(clientId)) || rows[0];
      const payrollYm = toYearMonth(match?.current_month);
      const endYm = minYearMonth(payrollYm || todayYm, todayYm);
      const months = applyMonthRange(startYm, endYm);
      if (!months.length) {
        showSnackbar("No months available for this branch.", "warning");
      }
    } catch {
      if (req !== monthLoadId.current) return;
      applyMonthRange(startYm, todayYm);
    }
  };

  const loadRecords = async (documentId = currentDoc, category = activeCat) => {
    if (!clientID || !selectedUnit || !documentId) {
      setRecords([]);
      return;
    }
    const result = await dispatch(getComplianceDocuments({
      client_id: clientID,
      unit_id: selectedUnit,
      category,
      document_id: documentId,
    }));
    if (result.success) {
      setRecords((result.data || []).map((row) => mapUploadRow(row, orgLabel, branchLabel)));
    } else {
      setRecords([]);
      showSnackbar(errText(result.message, "Could not load files."), "error");
    }
  };

  const handleClientChange = async (e) => {
    if (isClient) return;
    const id = e.target.value;
    setClientID(id);
    setSelectedUnit("");
    setUnitOptions([]);
    setRecords([]);
    setAllowedMonths([]);
    setReleaseOpen(false);
    setReleaseAllRows([]);
    setReleaseChecked({});
    resetPeriod();
    monthLoadId.current += 1;
    if (!id) {
      setUnitsLoading(false);
      return;
    }
    sessionStorage.setItem("selectedClient", id);
    setUnitsLoading(true);
    try {
      const response = await dispatch(getUnits(id));
      const rows = Array.isArray(response.data) ? response.data : [];
      setUnitOptions(rows.filter((u) => String(u.client_id) === String(id)));
    } catch {
      setUnitOptions([]);
      showSnackbar("Failed to load branches.", "error");
    } finally {
      setUnitsLoading(false);
    }
  };

  const handleUnitChange = (e) => {
    const unitId = e.target.value;
    setSelectedUnit(unitId);
    setRecords([]);
    setAllowedMonths([]);
    setReleaseOpen(false);
    setReleaseAllRows([]);
    setReleaseChecked({});
    setInlineList(null);
    setClientDocs([]);
    setCheckedItems({});
    resetPeriod();
    if (!unitId) {
      monthLoadId.current += 1;
      return;
    }
    const unit = unitOptions.find((u) => String(u.unit_id) === String(unitId));
    loadAllowedMonths(clientID, unit);
  };

  const handleYearChange = (e) => {
    const year = typeof e === "string" ? e : e.target.value;
    setSelectedYear(year);
    setSelectedMonth("");
    setPickedFile(null);
    setInlineList(null);
    setClientDocs([]);
    setCheckedItems({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDocChange = async (id) => {
    setCurrentDoc(id);
    resetDocInputs();
    setLoading(true);
    try {
      await Promise.all([loadSetup(activeCat, id), loadRecords(id, activeCat)]);
    } finally {
      setLoading(false);
    }
  };

  const applyFile = (file) => {
    if (!file) return;
    if (!fileLooksAllowed(file)) {
      showSnackbar("Use a PDF or JPEG/PNG file.", "warning");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showSnackbar("File must be 10 MB or less.", "warning");
      return;
    }
    setPickedFile(file);
  };

  const handleClearFile = () => {
    setPickedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleValidityChange = (value) => {
    setValidityType(value);
    if (value === "lifetime") {
      setValidUntil("");
      setFromDate("");
      setToDate("");
    } else if (RANGE_VALIDITY.has(value)) {
      setValidUntil("");
    } else {
      setFromDate("");
      setToDate("");
    }
  };

  const handleUpload = async () => {
    if (!pickedFile || !canUpload || uploading) return;
    if (!validityType) {
      showSnackbar("Validity type is required.", "warning");
      return;
    }
    const missing = liveFields.find(
      (f) => f.required && !String(answers[f.key] || "").trim()
    );
    if (missing) {
      showSnackbar(`${missing.label} is required.`, "warning");
      return;
    }
    if (validityType === "expiry" && !validUntil) {
      showSnackbar("Valid until is required.", "warning");
      return;
    }
    if (validityType === "monthly" && !validUntil) {
      showSnackbar("Due date is required.", "warning");
      return;
    }
    if (isRange && (!fromDate || !toDate)) {
      showSnackbar("From and To dates are required.", "warning");
      return;
    }
    if (isRange && toDate < fromDate) {
      showSnackbar("To date cannot be before From date.", "warning");
      return;
    }

    setUploading(true);
    setLoading(true);
    try {
      const result = await dispatch(uploadComplianceDocument({
        file: pickedFile,
        client_id: clientID,
        unit_id: selectedUnit,
        category: activeCat,
        document_id: currentDoc,
        document_name: docLabels[currentDoc],
        validity_type: validityType,
        year: selectedYear,
        month: selectedMonth,
        period_start: isRange ? fromDate : undefined,
        from_date: isRange ? fromDate : undefined,
        expiry_date: validityType === "lifetime" ? undefined : (isRange ? toDate : validUntil),
        to_date: isRange ? toDate : undefined,
        valid_until: !isRange && validityType !== "lifetime" ? validUntil : undefined,
        answers,
        fields_json: stripPlaceholders(liveFields),
      }));
      if (result.success) {
        showSnackbar(errText(result.message, "File uploaded."), "success");
        setAnswers({});
        setValidityType("");
        setValidUntil("");
        setFromDate("");
        setToDate("");
        handleClearFile();
        await loadRecords();
      } else {
        showSnackbar(errText(result.message, "Upload failed."), "error");
      }
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  const patchField = (index, patch) => {
    const next = liveFields.map((f, i) => (i === index ? { ...f, ...patch } : f));
    setLiveFields(next);
    persistSetup(next, validityType);
  };

  const deleteField = (index) => {
    const removed = liveFields[index];
    const next = liveFields.filter((_, i) => i !== index);
    setLiveFields(next);
    persistSetup(next, validityType);
    showSnackbar(`Removed ${removed.label}.`, "success");
  };

  const moveField = (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= liveFields.length) return;
    const next = liveFields.slice();
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item);
    setLiveFields(next);
    persistSetup(next, validityType);
  };

  const addField = () => {
    if (liveFields.length >= MAX_FIELDS) {
      showSnackbar(
        "You can add only 10 fields. Delete an existing field, then add a new one. You cannot add more than 10.",
        "error"
      );
      return;
    }
    const label = newLabel.trim();
    if (!label) {
      showSnackbar("Enter a field label.", "warning");
      return;
    }
    let key = slugKey(label);
    if (liveFields.some((f) => f.key === key)) key = `${key}_${Date.now()}`;
    const next = [
      ...liveFields,
      field(key, label, newType, {
        required: newRequired === "1",
        options: newOptions.split(",").map((s) => s.trim()).filter(Boolean),
      }),
    ];
    setLiveFields(next);
    persistSetup(next, validityType);
    setNewLabel("");
    setNewOptions("");
    setNewRequired("0");
    showSnackbar("Field added.", "success");
  };

  const handleResetType = () => {
    const next = defaultCatalog()[currentDoc] || [];
    setLiveFields(next);
    persistSetup(next, validityType || defaultValidity(activeCat));
    showSnackbar("Fields reset for this type.", "success");
  };

  const closePreview = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = "";
    setPreviewUrl("");
    setPreviewUnavailable(false);
    setPreviewType("");
    setImageZoom(1);
    imageZoomRef.current = 1;
    fitSizeRef.current = { w: 0, h: 0 };
    setFitSize({ w: 0, h: 0 });
    setViewRow(null);
  };

  const setImageZoomValue = (value) => {
    const next = Math.min(4, Math.max(0.5, Math.round(value * 10) / 10));
    imageZoomRef.current = next;
    setImageZoom(next);
  };

  const handleView = async (row) => {
    if (!row?.id) return;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = "";
    setImageZoom(1);
    imageZoomRef.current = 1;
    fitSizeRef.current = { w: 0, h: 0 };
    setFitSize({ w: 0, h: 0 });
    setPreviewUrl("");
    setPreviewUnavailable(false);
    setViewRow(row);
    setLoading(true);
    try {
      const response = await dispatch(viewComplianceDocument(row.id));
      const name = String(row.fileName || "");
      const contentType = String(response.contentType || "");
      const isPdf = contentType.includes("pdf") || /\.pdf$/i.test(name);
      const isImage = contentType.startsWith("image/") || /\.(jpe?g|png|gif|webp)$/i.test(name);
      if (response.success && response.blob) {
        const url = URL.createObjectURL(response.blob);
        previewUrlRef.current = url;
        setPreviewType(
          isPdf
            ? "application/pdf"
            : isImage
              ? contentType || "image/jpeg"
              : contentType || "application/octet-stream"
        );
        setPreviewUrl(isPdf ? `${url}#navpanes=0` : url);
      } else {
        setPreviewType("");
        setPreviewUnavailable(true);
      }
    } catch {
      setPreviewType("");
      setPreviewUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!viewRow || !previewType.includes("image/")) return undefined;
    const el = imageScrollerRef.current;
    if (!el) return undefined;

    const touchDistance = (touches) => {
      const a = touches[0];
      const b = touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };

    const onWheel = (e) => {
      e.preventDefault();
      const prev = imageZoomRef.current;
      const next = Math.min(4, Math.max(0.5, Math.round((prev + (e.deltaY < 0 ? 0.15 : -0.15)) * 10) / 10));
      if (next === prev) return;
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left + el.scrollLeft;
      const offsetY = e.clientY - rect.top + el.scrollTop;
      const ratio = next / prev;
      setImageZoomValue(next);
      requestAnimationFrame(() => {
        el.scrollLeft = offsetX * ratio - (e.clientX - rect.left);
        el.scrollTop = offsetY * ratio - (e.clientY - rect.top);
      });
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 2) return;
      pinchRef.current = {
        startDist: touchDistance(e.touches),
        startZoom: imageZoomRef.current,
      };
    };

    const onTouchMove = (e) => {
      if (e.touches.length !== 2) return;
      e.preventDefault();
      const startDist = pinchRef.current.startDist || 1;
      setImageZoomValue(pinchRef.current.startZoom * (touchDistance(e.touches) / startDist));
    };

    const onContextMenu = (e) => e.preventDefault();

    const stopDrag = () => {
      if (!dragRef.current.on) return;
      dragRef.current.on = false;
      el.classList.remove("is-panning");
    };

    const onMouseDown = (e) => {
      if (e.button !== 2 && e.button !== 0) return;
      if (e.button === 2) e.preventDefault();
      dragRef.current = {
        on: true,
        x: e.clientX,
        y: e.clientY,
        sl: el.scrollLeft,
        st: el.scrollTop,
      };
      el.classList.add("is-panning");
    };

    const onMouseMove = (e) => {
      if (!dragRef.current.on) return;
      e.preventDefault();
      el.scrollLeft = dragRef.current.sl - (e.clientX - dragRef.current.x);
      el.scrollTop = dragRef.current.st - (e.clientY - dragRef.current.y);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("contextmenu", onContextMenu);
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("contextmenu", onContextMenu);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);
    };
  }, [viewRow, previewType, previewUrl]);

  const handleDownload = async (rowOrId) => {
    const row = typeof rowOrId === "object" && rowOrId ? rowOrId : tableRows.find((item) => item.id === rowOrId);
    const id = row?.id || rowOrId;
    setLoading(true);
    try {
      const result = await dispatch(downloadComplianceDocument(id, row?.fileName));
      if (result.success) {
        showSnackbar("Download started.", "success");
      } else {
        showSnackbar(
          isClient
            ? clientDownloadErrorMessage(result.message)
            : errText(result.message, "Download failed."),
          isClient ? "warning" : "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isClient || !inlineList?.client_id || !inlineList?.unit_id) {
      return undefined;
    }
    if (!inlineList.allMonths && !inlineList.date) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setClientDocsLoading(true);
      try {
        const result = await dispatch(getComplianceDocuments({
          client_id: inlineList.client_id,
          unit_id: inlineList.unit_id,
        }));
        if (cancelled) return;
        if (!result.success) {
          showSnackbar(errText(result.message, "Could not load documents."), "error");
          setClientDocs([]);
          return;
        }
        const mapped = (result.data || []).map((row) => mapUploadRow(row, "", ""));
        const filtered = mapped
          .filter((row) => matchesInlinePeriod(row, inlineList))
          .sort((a, b) => String(rowYearMonth(a)).localeCompare(String(rowYearMonth(b))));
        setClientDocs(filtered);
        setCheckedItems({});
        if (!filtered.length) {
          showSnackbar("No documents for this period.", "warning");
        }
      } catch {
        if (!cancelled) {
          showSnackbar("Could not load documents.", "error");
          setClientDocs([]);
        }
      } finally {
        if (!cancelled) setClientDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isClient, inlineList, dispatch, showSnackbar]);

  const handleClientViewSubmit = (e) => {
    e.preventDefault();
    if (!clientID || !selectedUnit || !selectedYear || !selectedMonth) {
      showSnackbar("Please select branch, year, and month.", "error");
      return;
    }
    if (selectedMonth === ALL_MONTHS) {
      if (!allowedMonths.some((ym) => ym.startsWith(`${selectedYear}-`))) {
        showSnackbar("No released documents for this year.", "warning");
        return;
      }
      setInlineList({
        client_id: clientID,
        unit_id: selectedUnit,
        date: selectedYear,
        year: selectedYear,
        allMonths: true,
      });
      return;
    }
    const ym = `${selectedYear}-${selectedMonth}`;
    if (!allowedMonths.includes(ym)) {
      showSnackbar("Select a month that already has released documents.", "error");
      return;
    }
    setInlineList({
      client_id: clientID,
      unit_id: selectedUnit,
      date: ym,
      year: selectedYear,
      allMonths: false,
    });
  };

  const handleClientCheckAll = (e) => {
    const checked = e.target.checked;
    if (!checked) {
      setCheckedItems({});
      return;
    }
    const next = {};
    clientDocs.forEach((row) => {
      if (row.id) next[String(row.id)] = true;
    });
    setCheckedItems(next);
  };

  const handleClientCheckChange = (id) => {
    const key = String(id);
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClientBulkDownload = async () => {
    const selected = clientDocs.filter((row) => checkedItems[String(row.id)]);
    if (!selected.length) {
      showSnackbar("select the row", "warning");
      return;
    }
    if (selected.length === 1) {
      await handleDownload(selected[0]);
      return;
    }
    const ids = selected.map((row) => row.id).filter(Boolean);
    const branch = unitOptions.find((unit) => String(unit.unit_id) === String(selectedUnit));
    const branchPart = String(branchOptionLabel(branch) || "documents").replace(/[<>:"/\\|?*]/g, "").trim();
    const period = (
      inlineList?.allMonths
        ? periodHeading(inlineList)
        : monthLabel(selectedMonth, selectedYear)
    ).replace(/\s+/g, " ");
    const zipName = `${branchPart}_${period || "documents"}_${ids.length}_documents.zip`;
    setZipBusy(true);
    setLoading(true);
    try {
      const result = await dispatch(downloadComplianceDocumentsZip(ids, zipName));
      if (result.success) showSnackbar("Documents downloaded successfully.", "success");
      else showSnackbar(clientDownloadErrorMessage(result.message), "warning");
    } finally {
      setZipBusy(false);
      setLoading(false);
    }
  };

  const handleDelete = (row) => {
    setDeleteRow(row);
  };

  const handleDeleteClose = () => {
    setDeleteRow(null);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteRow?.id) return;
    const id = deleteRow.id;
    setDeleteRow(null);
    setLoading(true);
    try {
      const result = await dispatch(deleteComplianceDocument(id));
      if (result.success) {
        showSnackbar(errText(result.message, "Deleted successfully."), "success");
        if (viewRow?.id === id) closePreview();
        setReleaseAllRows((prev) => prev.filter((row) => row.id !== id));
        await loadRecords();
      } else {
        showSnackbar(errText(result.message, "Delete failed."), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const applyReleasedIds = (ids) => {
    const released = new Set(ids.map(String));
    const mark = (row) => (
      released.has(String(row.id)) ? { ...row, is_released: true } : row
    );
    setReleaseAllRows((prev) => prev.map(mark));
    setRecords((prev) => prev.map(mark));
    setReleaseChecked((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { delete next[id]; });
      return next;
    });
  };

  const openReleaseDialog = async () => {
    if (!hasOrgBranch) {
      showSnackbar("Select organization and branch first.", "warning");
      return;
    }
    setReleaseOpen(true);
    setReleaseChecked({});
    setReleaseYear(selectedYear || "");
    setReleaseMonth(selectedMonth || "");
    setLoading(true);
    try {
      const result = await dispatch(getComplianceDocuments({
        client_id: clientID,
        unit_id: selectedUnit,
      }));
      if (result.success) {
        setReleaseAllRows((result.data || []).map((row) => mapUploadRow(row, orgLabel, branchLabel)));
      } else {
        setReleaseAllRows([]);
        showSnackbar(errText(result.message, "Could not load files."), "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const closeReleaseDialog = () => {
    setReleaseOpen(false);
    setReleaseChecked({});
  };

  const handleReleaseYearChange = (year) => {
    setReleaseYear(year);
    setReleaseMonth("");
    setReleaseChecked({});
  };

  const toggleReleaseRow = (id, checked) => {
    setReleaseChecked((prev) => ({ ...prev, [id]: checked }));
  };

  const toggleReleaseAll = (checked) => {
    const next = {};
    if (checked) {
      releaseMonthRows.forEach((row) => { next[row.id] = true; });
    }
    setReleaseChecked(next);
  };

  const handleReleaseIds = async (ids) => {
    const unique = [...new Set(ids.map((id) => Number(id)).filter(Boolean))];
    if (!unique.length) {
      showSnackbar("Select the row", "warning");
      return;
    }
    setReleasing(true);
    setLoading(true);
    try {
      const result = unique.length === 1
        ? await dispatch(releaseComplianceDocument(unique[0]))
        : await dispatch(releaseComplianceDocuments(unique));
      if (result.success) {
        showSnackbar(errText(result.message, unique.length === 1 ? "Document released." : "Documents released."), "success");
        applyReleasedIds(unique);
      } else {
        showSnackbar(errText(result.message, "Release failed."), "error");
      }
    } finally {
      setReleasing(false);
      setLoading(false);
    }
  };

  const orgName = selectedClient?.client_name || "Your organization";
  const orgState = String(selectedClient?.state_name || selectedBranch?.state || selectedBranch?.state_name || "").trim();
  const selectedBranchLabel = (() => {
    const name = String(selectedBranch?.unit_name || "").trim();
    const location = unitLocation(selectedBranch);
    if (name && location) return `${name} (${location})`;
    return name || location || "";
  })();
  const branchPlace = formatBranchPlace(selectedBranch);
  const periodDisabled = !selectedUnit || !allowedMonths.length;
  const canViewPeriod = Boolean(selectedYear && selectedMonth);
  const selectedIds = Object.keys(checkedItems).filter((id) => checkedItems[id]);
  const allChecked =
    clientDocs.length > 0 &&
    clientDocs.every((row) => row.id && checkedItems[String(row.id)]);
  const actionsLocked = Boolean(busyDocId) || zipBusy;

  useLayoutEffect(() => {
    if (!isClient) return undefined;
    const el = stickyRef.current;
    const shell = el?.parentElement;
    if (!el || !shell) return undefined;
    const apply = () => {
      shell.style.setProperty("--client-sticky-h", `${el.offsetHeight}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [isClient, inlineList, clientDocsLoading, clientDocs.length]);

  const previewModal = viewRow ? createPortal(
    <div className="compliance-documents-page cd-portal">
      <div
        className="cd-modal-bg"
        onClick={(e) => {
          if (e.target === e.currentTarget) closePreview();
        }}
        onWheel={stopPageScroll}
        onTouchMove={stopPageScroll}
      >
        <div className="cd-modal">
          <div className="cd-modal-head">
            <h2>Submission report</h2>
            <div className="cd-modal-actions">
              <button type="button" className="cd-btn ghost" onClick={() => handleDownload(viewRow)}>Download</button>
              <button type="button" className="cd-btn ghost" onClick={closePreview}>Close</button>
            </div>
          </div>
          <div className="cd-meta-row">
            <div>
              <span>Start period</span>
              <strong>{periodDisplay(viewRow)}</strong>
            </div>
            <div>
              <span>Validity</span>
              <strong>{viewRow.validity_type || "—"}</strong>
            </div>
            <div>
              <span>Expiry / To</span>
              <strong>{formatDateDdMmYyyy(viewRow.expiry_date) || "—"}</strong>
            </div>
          </div>
          {previewUnavailable ? (
            <div className="cd-preview-unavailable">
              <p>This document isn’t available to view right now.</p>
              <p>Please try again later, or ask your administrator if you still need it.</p>
            </div>
          ) : previewUrl ? (
            previewType.includes("image/") ? (
              <div
                ref={imageScrollerRef}
                className={`cd-preview-file cd-image-scroller${imageZoom > 1 ? " is-zoomed" : ""}`}
                title="Wheel to zoom. Click or right-click and drag to move."
              >
                <img
                  src={previewUrl}
                  alt={viewRow.document_name || "Document"}
                  draggable={false}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    const box = imageScrollerRef.current;
                    if (!box || !img.naturalWidth) return;
                    const scale = Math.min(
                      box.clientWidth / img.naturalWidth,
                      box.clientHeight / img.naturalHeight,
                      1
                    );
                    const next = {
                      w: img.naturalWidth * scale,
                      h: img.naturalHeight * scale,
                    };
                    fitSizeRef.current = next;
                    setFitSize(next);
                  }}
                  style={
                    fitSize.w
                      ? {
                          width: fitSize.w * imageZoom,
                          height: fitSize.h * imageZoom,
                          maxWidth: "none",
                          maxHeight: "none",
                        }
                      : {
                          maxWidth: "100%",
                          maxHeight: "100%",
                          width: "auto",
                          height: "auto",
                          objectFit: "contain",
                        }
                  }
                />
              </div>
            ) : (
              <iframe
                className="cd-preview-file"
                title={viewRow.fileName || viewRow.document_name || "Document"}
                src={previewUrl}
              />
            )
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (isClient) {
    return (
      <div className="container-fluid generated-forms-page generated-forms-page-client">
        <div className="generated-forms-client-shell">
          <div className="generated-forms-client-title">
            <h1>Select your branch, year, and month to open the related documents.</h1>
          </div>

          <div className="generated-forms-client-card">
            <div className="generated-forms-client-org" aria-label="Organization">
              <div className="generated-forms-client-org-icon">
                <FaBuilding />
              </div>
              <div className="generated-forms-client-org-copy">
                <span>Organization</span>
                <strong>{orgName}</strong>
                {orgState ? <em>{orgState}</em> : null}
              </div>
            </div>
          </div>

          <div className="generated-forms-client-sticky-block" ref={stickyRef}>
            <form className="generated-forms-client-form" onSubmit={handleClientViewSubmit} data-tour="docs-filters">
              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="cd-client-branch">
                  <FaMapMarkerAlt /> Branch
                </label>
                <select
                  id="cd-client-branch"
                  className="form-select"
                  value={selectedUnit}
                  onChange={handleUnitChange}
                  disabled={!clientID}
                >
                  <option value="">Select branch</option>
                  {unitOptions.map((unit) => (
                    <option key={unit.unit_id} value={unit.unit_id}>
                      {branchOptionLabel(unit)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="cd-client-year">Year</label>
                <select
                  id="cd-client-year"
                  className="form-select"
                  value={selectedYear}
                  onChange={(ev) => handleYearChange(ev.target.value)}
                  disabled={periodDisabled}
                >
                  <option value="">Select year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-field">
                <label className="form-label" htmlFor="cd-client-month">Month</label>
                <select
                  id="cd-client-month"
                  className="form-select"
                  value={selectedMonth}
                  onChange={(ev) => {
                    setSelectedMonth(ev.target.value);
                    setInlineList(null);
                    setClientDocs([]);
                    setCheckedItems({});
                  }}
                  disabled={periodDisabled || !selectedYear}
                >
                  <option value="">Select month</option>
                  <option value={ALL_MONTHS}>All months</option>
                  {monthOptions.map((mm) => (
                    <option key={mm} value={mm}>
                      {MONTH_NAMES[parseInt(mm, 10) - 1]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="generated-forms-client-actions">
                <button
                  type="submit"
                  className="btn generated-forms-client-submit"
                  disabled={!clientID || !selectedUnit || !canViewPeriod}
                >
                  View documents <FaArrowRight />
                </button>
              </div>
            </form>

            {inlineList && !clientDocsLoading && clientDocs.length > 0 ? (
              <div className="generated-forms-client-list-head">
                <strong>
                  {selectedBranchLabel}
                  {periodHeading(inlineList) ? ` · ${periodHeading(inlineList)}` : ""}
                </strong>
                <div className="generated-forms-client-list-tools">
                  <button
                    type="button"
                    className="generated-forms-client-bulk-download"
                    title={selectedIds.length ? `Download ${selectedIds.length} selected` : "select the row"}
                    onClick={handleClientBulkDownload}
                    disabled={zipBusy}
                  >
                    <img src={bulkDownloadIcon} alt="Download" />
                  </button>
                  <span>{clientDocs.length} {clientDocs.length === 1 ? "document" : "documents"}</span>
                </div>
              </div>
            ) : null}
          </div>

          {inlineList ? (
            <div className={`generated-forms-client-workspace${clientDocsLoading || clientDocs.length === 0 ? "" : " is-filled"}`}>
              {clientDocsLoading ? (
                <div className="generated-forms-client-list-empty">
                  <p>Loading documents…</p>
                </div>
              ) : clientDocs.length === 0 ? (
                <div className="generated-forms-client-list-empty">
                  <div className="generated-forms-client-workspace-icon">
                    <FaFileAlt />
                  </div>
                  <p>{inlineList.allMonths ? "No documents for this branch and year." : "No documents for this branch and month."}</p>
                </div>
              ) : (
                <div className="generated-forms-client-table-scroll" data-tour="docs-list">
                  <table className="generated-forms-client-list">
                    <colgroup>
                      <col className="generated-forms-client-col-check" />
                      <col className="generated-forms-client-col-form" />
                      <col className="generated-forms-client-col-location" />
                      <col className="generated-forms-client-col-month" />
                      <col className="generated-forms-client-col-action" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="generated-forms-client-check">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={handleClientCheckAll}
                            disabled={actionsLocked}
                            aria-label="Select all documents"
                          />
                        </th>
                        <th>Document</th>
                        <th>Location</th>
                        <th>Month</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientDocs.map((row) => {
                        const busy = busyDocId === row.id;
                        return (
                          <tr key={row.id}>
                            <td className="generated-forms-client-check">
                              <input
                                type="checkbox"
                                checked={!!checkedItems[String(row.id)]}
                                onChange={() => handleClientCheckChange(row.id)}
                                disabled={actionsLocked || !row.id}
                                aria-label={`Select ${row.document_name || docLabels[row.doc] || "document"}`}
                              />
                            </td>
                            <td>{row.document_name || docLabels[row.doc] || row.doc}</td>
                            <td>{branchPlace || "—"}</td>
                            <td>{monthLabel(row.month, row.year)}</td>
                            <td className="generated-forms-client-actions-cell">
                              <div className="generated-forms-client-row-actions">
                                <button
                                  type="button"
                                  title="View"
                                  disabled={actionsLocked}
                                  onClick={() => {
                                    setBusyDocId(row.id);
                                    handleView(row);
                                    setBusyDocId(null);
                                  }}
                                >
                                  <FaEye />
                                </button>
                                <button
                                  type="button"
                                  title="Download"
                                  disabled={actionsLocked}
                                  onClick={async () => {
                                    setBusyDocId(row.id);
                                    await handleDownload(row);
                                    setBusyDocId(null);
                                  }}
                                >
                                  <img src={downloadIcon} alt="Download" />
                                </button>
                              </div>
                              {busy || zipBusy ? <span className="visually-hidden">Working</span> : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="generated-forms-client-workspace">
              <div className="generated-forms-client-docs" aria-hidden="true">
                <div className="generated-forms-client-doc">
                  <span /><span /><span /><span />
                </div>
                <div className="generated-forms-client-doc is-active">
                  <span /><span /><span /><span />
                </div>
                <div className="generated-forms-client-doc">
                  <span /><span /><span /><span />
                </div>
              </div>
              <div className="generated-forms-client-workspace-copy">
                <div className="generated-forms-client-workspace-icon">
                  <FaFileAlt />
                </div>
                <p>Documents will appear here after you click View documents.</p>
              </div>
            </div>
          )}
        </div>
        {previewModal}
      </div>
    );
  }

  return (
    <div className="compliance-documents-page">
      <h1>Compliance Documents</h1>
      <p className="cd-sub">
        Pick organization and branch, then month. Extra fields are for the selected document type only.
      </p>

      <div className="cd-card cd-filters">
        <div className={`cd-row4${hasOrgBranch ? " is-period" : ""}`}>
          <div className="cd-field">
            <label htmlFor="cd-org">Organization</label>
            <select id="cd-org" value={clientID} onChange={handleClientChange}>
              <option value="">Select Organization</option>
              {clientOptions.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {orgOptionLabel(c)}
                </option>
              ))}
            </select>
          </div>
          <div className="cd-field">
            <label htmlFor="cd-branch">Branch</label>
            <select
              id="cd-branch"
              value={selectedUnit}
              onChange={handleUnitChange}
              disabled={!clientID || unitsLoading || !unitOptions.length}
            >
              <option value="">Select Branch</option>
              {unitOptions.map((unit) => (
                <option key={unit.unit_id} value={unit.unit_id}>
                  {branchOptionLabel(unit)}
                </option>
              ))}
            </select>
          </div>
          {hasOrgBranch ? (
            <>
              <div className="cd-field">
                <label htmlFor="cd-year">Year</label>
                <select
                  id="cd-year"
                  value={selectedYear}
                  onChange={handleYearChange}
                  disabled={!allowedMonths.length}
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="cd-field">
                <label htmlFor="cd-month">Month</label>
                <select
                  id="cd-month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={!selectedYear || !monthOptions.length}
                >
                  <option value="">Select Month</option>
                  {monthOptions.map((mm) => (
                    <option key={mm} value={mm}>
                      {MONTH_NAMES[parseInt(mm, 10) - 1]}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {periodReady ? (
        <div className="cd-workspace">
          <div className="cd-card cd-doc-picker">
            {!isClient ? (
              <div className="cd-picker-toolbar">
                <button type="button" className="cd-add-cta" onClick={openCatalogDialog}>
                  <FaPlus />
                  Add
                </button>
              </div>
            ) : null}
            <div className={`cd-cat-scroll${canScrollCatsLeft ? " has-left" : ""}${canScrollCatsRight ? " has-right" : ""}`}>
              {canScrollCatsLeft ? (
                <button
                  type="button"
                  className="cd-cat-arrow left"
                  aria-label="Scroll categories left"
                  onClick={() => scrollCats(-1)}
                >
                  <FaChevronLeft />
                </button>
              ) : null}
              <div className="cd-cat-tabs" ref={catTabsRef} onScroll={updateCatScroll}>
                {categories.map((cat) => (
                  <button
                    key={cat.slug || cat.code}
                    type="button"
                    className={`cd-cat-tab${activeCat === cat.slug || activeCat === cat.code ? " active" : ""}`}
                    onClick={() => {
                      setActiveCat(cat.slug || cat.code);
                      setCurrentDoc("");
                      resetDocInputs();
                    }}
                  >
                    {cat.label || cat.name}
                  </button>
                ))}
              </div>
              {canScrollCatsRight ? (
                <button
                  type="button"
                  className="cd-cat-arrow right"
                  aria-label="Scroll categories right"
                  onClick={() => scrollCats(1)}
                >
                  <FaChevronRight />
                </button>
              ) : null}
            </div>
            <div className="cd-type-pills">
              {typeList.map((doc) => (
                <button
                  key={doc.slug || doc.code}
                  type="button"
                  className={`cd-type-btn${currentDoc === doc.slug || currentDoc === doc.code ? " active" : ""}`}
                  onClick={() => handleDocChange(doc.slug || doc.code)}
                >
                  {doc.label || doc.name}
                </button>
              ))}
            </div>
          </div>

          <div className="cd-card cd-upload-panel">
            {!canUpload ? (
              <div className="cd-blank">Pick a document type above (e.g. Trade licence).</div>
            ) : (
              <div>
                <div className="cd-upload-head">
                  <div className="cd-upload-head-text">
                    <p className="cd-upload-title">{docLabels[currentDoc]}</p>
                    <p className="cd-upload-meta">
                      {orgLabel}  ·  {branchLabel}  ·  {monthLabel(selectedMonth, selectedYear)}
                    </p>
                  </div>
                  {!isClient && hasValidity ? (
                    <button
                      type="button"
                      className="cd-btn modify"
                      onClick={() => setFieldsOpen(true)}
                    >
                      Modify your own fields
                    </button>
                  ) : null}
                </div>

                <div className={`cd-date-row${isRange ? " is-range" : validityType === "expiry" || validityType === "monthly" ? " is-pair" : ""}`}>
                  <div className="cd-field">
                    <label htmlFor="cd-validity">Validity type *</label>
                    <select
                      id="cd-validity"
                      value={validityType}
                      onChange={(e) => handleValidityChange(e.target.value)}
                    >
                      <option value="">Select validity type</option>
                      {VALIDITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {validityType === "expiry" ? (
                    <div className="cd-field">
                      <label htmlFor="cd-valid-until">Valid until *</label>
                      <input
                        id="cd-valid-until"
                        type="date"
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {validityType === "monthly" ? (
                    <div className="cd-field">
                      <label htmlFor="cd-due-date">Due date *</label>
                      <input
                        id="cd-due-date"
                        type="date"
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {isRange ? (
                    <>
                      <div className="cd-field">
                        <label htmlFor="cd-from">From *</label>
                        <input
                          id="cd-from"
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>
                      <div className="cd-field">
                        <label htmlFor="cd-to">To *</label>
                        <input
                          id="cd-to"
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>
                    </>
                  ) : null}
                </div>

                {hasValidity ? (
                  <>
                {liveFields.length ? (
                  <div className="cd-fields-grid">
                    {liveFields.map((f) => (
                      <div key={f.key} className={`cd-field${f.input_type === "textarea" ? " full" : ""}`}>
                        <label htmlFor={`cd-ans-${f.key}`}>
                          {f.label}{f.required ? " *" : ""}
                        </label>
                        <FieldInput
                          fieldDef={f}
                          value={answers[f.key]}
                          onChange={setAnswer}
                          states={stateOptions}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="cd-upload-meta" style={{ marginBottom: 12 }}>
                    No extra fields (file only). Use Modify your own fields to add some.
                  </p>
                )}

                <div
                  className={`cd-slot${pickedFile ? " filled" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    applyFile(e.dataTransfer.files?.[0]);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                  }}
                >
                  <div className="cd-slot-icon">↑</div>
                  <div className="cd-slot-copy">
                    <b>{pickedFile ? pickedFile.name : "Drop PDF or JPEG here"}</b>
                    <small>or click to choose a file · max 10 MB</small>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => applyFile(e.target.files?.[0])}
                />
                <div className="cd-actions">
                  <button type="button" className="cd-btn ghost" onClick={handleClearFile}>
                    Clear
                  </button>
                  <button
                    type="button"
                    className="cd-btn primary"
                    onClick={handleUpload}
                    disabled={!pickedFile || uploading}
                  >
                    Upload
                  </button>
                </div>
                  </>
                ) : (
                  <p className="cd-upload-meta">Select validity type to continue.</p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="cd-card cd-records">
        <div className="cd-records-head">
          <h2>Existing certificates</h2>
          <div className="cd-records-tools">
            {!isClient ? (
              <button
                type="button"
                className="cd-btn ghost"
                disabled={!hasOrgBranch || releasing}
                onClick={openReleaseDialog}
              >
                Release
              </button>
            ) : null}
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              disabled={!uploadedMonths.length}
            >
              <option value="">All months</option>
              {uploadedMonths.map((ym) => (
                <option key={ym} value={ym}>
                  {monthLabel(ym.slice(5, 7), ym.slice(0, 4))}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!canUpload ? (
          <div className="cd-blank">
            Select organization, branch, year and month, then a document type.
          </div>
        ) : (
          <div className="cd-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Organization</th>
                  <th>Branch</th>
                  <th>Start period</th>
                  <th>Expiry / To</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.length ? (
                  tableRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.document_name || docLabels[row.doc] || row.doc}</td>
                      <td title={orgLabel}>{compactOrgLabel(selectedClient, row.client_id)}</td>
                      <td title={branchOptionLabel(selectedBranch) || row.unit_label}>
                        {compactBranchLabel(selectedBranch, row.unit_id)}
                      </td>
                      <td>{periodDisplay(row)}</td>
                      <td>{formatDateDdMmYyyy(row.expiry_date) || "—"}</td>
                      <td>
                        <div className="cd-row-actions">
                          <button
                            type="button"
                            className="cd-icon-btn"
                            title="View"
                            aria-label="View"
                            onClick={() => handleView(row)}
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className="cd-icon-btn"
                            title="Download"
                            aria-label="Download"
                            onClick={() => handleDownload(row)}
                          >
                            <FaDownload />
                          </button>
                          <button
                            type="button"
                            className="cd-icon-btn danger"
                            title="Delete"
                            aria-label="Delete"
                            onClick={() => handleDelete(row)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>No files for this filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {fieldsOpen && canUpload && hasValidity ? createPortal(
        <div className="compliance-documents-page cd-portal">
        <div
          className="cd-modal-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setFieldsOpen(false);
          }}
          onWheel={stopPageScroll}
          onTouchMove={stopPageScroll}
        >
          <div className="cd-modal-fields" role="dialog" aria-labelledby="cd-fields-title">
            <div className="cd-modal-head">
              <div>
                <h2 id="cd-fields-title">Modify fields — {docLabels[currentDoc]}</h2>
                <p>Only this document type. Other types are not shown.</p>
              </div>
              <div className="cd-modal-actions">
                <button
                  type="button"
                  className="cd-btn ghost"
                  onClick={handleResetType}
                >
                  Reset this type
                </button>
                <button type="button" className="cd-btn primary" style={{ flex: "none" }} onClick={() => setFieldsOpen(false)}>
                  Close
                </button>
              </div>
            </div>
            <div className="cd-hint">
              Add, edit, delete or move fields for {docLabels[currentDoc]} only.
              Maximum 10 fields ({liveFields.length}/10).
            </div>
            <div className="cd-editor-scroll">
            <div className="cd-editor-row head">
              <div>Label</div>
              <div>Type</div>
              <div>Dropdown options</div>
              <div>Required</div>
              <div>Move</div>
              <div></div>
            </div>
            {liveFields.length ? liveFields.map((f, i) => (
              <div key={f.key} className="cd-editor-row">
                <input
                  type="text"
                  defaultValue={f.label}
                  onBlur={(e) => {
                    const label = e.target.value.trim();
                    if (label && label !== f.label) patchField(i, { label });
                  }}
                />
                <select
                  value={f.input_type}
                  onChange={(e) => patchField(i, { input_type: e.target.value })}
                >
                  {INPUT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  defaultValue={(f.options || []).join(", ")}
                  disabled={f.input_type !== "select"}
                  placeholder="A, B, C"
                  onBlur={(e) => {
                    if (f.input_type !== "select") return;
                    patchField(i, {
                      options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    });
                  }}
                />
                <select
                  value={f.required ? "1" : "0"}
                  onChange={(e) => patchField(i, { required: e.target.value === "1" })}
                >
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
                <div className="cd-move-btns">
                  <button type="button" className="cd-btn move" disabled={i === 0} onClick={() => moveField(i, -1)}>↑</button>
                  <button type="button" className="cd-btn move" disabled={i === liveFields.length - 1} onClick={() => moveField(i, 1)}>↓</button>
                </div>
                <button type="button" className="cd-btn danger" onClick={() => deleteField(i)}>Delete</button>
              </div>
            )) : (
              <div className="cd-blank" style={{ margin: "8px 0" }}>
                No extra fields. Upload is file-only until you add one.
              </div>
            )}
            </div>
            <div className="cd-add-box">
              <div>
                <label htmlFor="cd-new-label">New field label</label>
                <input
                  id="cd-new-label"
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Issue date"
                />
              </div>
              <div>
                <label htmlFor="cd-new-type">Input type</label>
                <select id="cd-new-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
                  {INPUT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cd-new-options">Dropdown options (comma)</label>
                <input
                  id="cd-new-options"
                  type="text"
                  value={newOptions}
                  onChange={(e) => setNewOptions(e.target.value)}
                  disabled={newType !== "select"}
                  placeholder="Monthly, Annual"
                />
              </div>
              <div>
                <label htmlFor="cd-new-req">Required</label>
                <select id="cd-new-req" value={newRequired} onChange={(e) => setNewRequired(e.target.value)}>
                  <option value="0">No</option>
                  <option value="1">Yes</option>
                </select>
              </div>
              <button
                type="button"
                className="cd-btn primary"
                style={{ flex: "none", height: 38 }}
                onClick={addField}
                disabled={liveFields.length >= MAX_FIELDS}
              >
                Add field
              </button>
            </div>
          </div>
        </div>
        </div>,
        document.body
      ) : null}

      {previewModal}

      {releaseOpen ? createPortal(
        <div className="compliance-documents-page cd-portal">
        <div
          className="cd-modal-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeReleaseDialog();
          }}
          onWheel={stopPageScroll}
          onTouchMove={stopPageScroll}
        >
          <div className="cd-modal-fields cd-modal-release" role="dialog" aria-labelledby="cd-release-title">
            <div className="cd-modal-head">
              <h2 id="cd-release-title">Release documents</h2>
              <div className="cd-modal-actions">
                <button type="button" className="cd-btn ghost" onClick={closeReleaseDialog}>
                  Close
                </button>
                <button
                  type="button"
                  className="cd-btn primary"
                  style={{ flex: "none" }}
                  disabled={releasing || !releaseSelectedIds.length}
                  onClick={() => handleReleaseIds(releaseSelectedIds)}
                >
                  {releaseHeaderLabel}
                </button>
              </div>
            </div>
            <div className="cd-release-who">
              <span className="cd-release-who-item">
                <em>Organization</em>
                <strong>{orgLabel || "—"}</strong>
              </span>
              <span className="cd-release-who-item">
                <em>Branch</em>
                <strong>{branchLabel || "—"}</strong>
              </span>
            </div>
            <div className="cd-release-filters">
              <div className="cd-field">
                <label htmlFor="cd-release-year">Year</label>
                <select
                  id="cd-release-year"
                  value={releaseYear}
                  onChange={(e) => handleReleaseYearChange(e.target.value)}
                  disabled={!allowedMonths.length}
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="cd-field">
                <label htmlFor="cd-release-month">Month</label>
                <select
                  id="cd-release-month"
                  value={releaseMonth}
                  onChange={(e) => {
                    setReleaseMonth(e.target.value);
                    setReleaseChecked({});
                  }}
                  disabled={!releaseYear || !releaseMonthOptions.length}
                >
                  <option value="">Select Month</option>
                  {releaseMonthOptions.map((mm) => (
                    <option key={mm} value={mm}>
                      {MONTH_NAMES[parseInt(mm, 10) - 1]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="cd-editor-scroll">
              {!releaseYear || !releaseMonth ? (
                <div className="cd-blank">Select year and month to see uploaded documents.</div>
              ) : releaseMonthRows.length ? (
                <div className="cd-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th className="cd-check-col">
                          <input
                            type="checkbox"
                            checked={releaseAllSelected}
                            disabled={!releaseMonthRows.length || releasing}
                            onChange={(e) => toggleReleaseAll(e.target.checked)}
                            aria-label="Select all"
                          />
                        </th>
                        <th>Document</th>
                        <th>Start period</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {releaseMonthRows.map((row) => (
                        <tr key={row.id}>
                          <td className="cd-check-col">
                            <input
                              type="checkbox"
                              checked={Boolean(releaseChecked[row.id])}
                              disabled={releasing}
                              onChange={(e) => toggleReleaseRow(row.id, e.target.checked)}
                              aria-label={`Select ${row.document_name}`}
                            />
                          </td>
                          <td>{row.document_name || docLabels[row.doc] || row.doc}</td>
                          <td>{periodDisplay(row)}</td>
                          <td>
                            <span className={row.is_released ? "cd-tag cd-tag-ok" : "cd-tag"}>
                              {row.is_released ? "Released" : "Not released"}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="cd-btn ghost"
                              style={{ height: 30, padding: "0 12px", width: "auto" }}
                              disabled={releasing}
                              onClick={() => handleReleaseIds([row.id])}
                            >
                              {row.is_released ? "Re-release" : "Release"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="cd-blank">No documents uploaded for this month.</div>
              )}
            </div>
          </div>
        </div>
        </div>,
        document.body
      ) : null}

      <Dialog
        open={catalogDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={catalogSaving ? undefined : closeCatalogDialog}
        className="cd-catalog-dialog-root"
        PaperProps={{ className: "cd-catalog-dialog" }}
      >
        <DialogTitle className="cd-catalog-dialog-title">
          <span className="cd-catalog-dialog-icon"><FaPlus /></span>
          <span className="cd-catalog-dialog-heading">
            <strong>Add to catalog</strong>
            <small>Create a new category tab, or add a document type under an existing one.</small>
          </span>
        </DialogTitle>
        <DialogContent className="cd-catalog-dialog-body">
          <div className="cd-catalog-modes" role="tablist" aria-label="Add mode">
            <button
              type="button"
              role="tab"
              className={`cd-catalog-mode${catalogParentSlug !== CATALOG_NEW ? " active" : ""}`}
              disabled={catalogSaving}
              onClick={() => {
                setCatalogParentSlug(activeCat || categories[0]?.slug || CATALOG_NEW);
                setCatalogChecked({});
              }}
            >
              Existing category
            </button>
            <button
              type="button"
              role="tab"
              className={`cd-catalog-mode${catalogParentSlug === CATALOG_NEW ? " active" : ""}`}
              disabled={catalogSaving}
              onClick={() => {
                setCatalogParentSlug(CATALOG_NEW);
                setCatalogChecked({});
              }}
            >
              New category
            </button>
          </div>
          {catalogParentSlug === CATALOG_NEW ? (
            <label className="cd-catalog-label" htmlFor="cd-catalog-category">
              Category name
              <input
                id="cd-catalog-category"
                className="cd-catalog-name"
                type="text"
                value={catalogCategoryName}
                onChange={(e) => setCatalogCategoryName(e.target.value)}
                placeholder="e.g. Registrations & Licences"
                disabled={catalogSaving}
              />
            </label>
          ) : (
            <label className="cd-catalog-label" htmlFor="cd-catalog-parent">
              Category
              <select
                id="cd-catalog-parent"
                className="cd-catalog-name"
                value={catalogParentSlug}
                onChange={(e) => {
                  setCatalogParentSlug(e.target.value);
                  setCatalogChecked({});
                }}
                disabled={catalogSaving}
              >
                {categories.map((cat) => (
                  <option key={cat.slug || cat.code} value={cat.slug || cat.code}>{cat.label || cat.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="cd-catalog-label" htmlFor="cd-catalog-type">
            Document type
            <input
              id="cd-catalog-type"
              className="cd-catalog-name"
              type="text"
              value={catalogTypeName}
              onChange={(e) => setCatalogTypeName(e.target.value)}
              placeholder="e.g. Trade licence"
              autoComplete="off"
              disabled={catalogSaving}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCatalogCreate();
                }
              }}
            />
          </label>
          {catalogParentSlug !== CATALOG_NEW && catalogSelectedCat ? (
            <div className="cd-catalog-delete-box">
              {catalogSelectedTypes.length ? (
                <>
                  <div className="cd-catalog-delete-toolbar">
                    <label className="cd-catalog-check">
                      <input
                        type="checkbox"
                        checked={
                          catalogSelectedTypes.some((doc) => catalogItemId(doc))
                          && catalogSelectedTypes.every((doc) => {
                            const id = catalogItemId(doc);
                            return !id || catalogChecked[id];
                          })
                        }
                        disabled={catalogSaving || catalogDeleting || !catalogSelectedTypes.some((doc) => catalogItemId(doc))}
                        onChange={(e) => {
                          const next = {};
                          if (e.target.checked) {
                            catalogSelectedTypes.forEach((doc) => {
                              const id = catalogItemId(doc);
                              if (id) next[id] = true;
                            });
                          }
                          setCatalogChecked(next);
                        }}
                        aria-label="Select all document types"
                      />
                      Select all
                    </label>
                    <button
                      type="button"
                      className="cd-icon-btn danger"
                      title="Delete selected"
                      aria-label="Delete selected"
                      disabled={
                        catalogSaving
                        || catalogDeleting
                        || !catalogSelectedTypes.some((doc) => catalogChecked[catalogItemId(doc)])
                      }
                      onClick={() => catalogDeleteItems(
                        catalogSelectedTypes
                          .filter((doc) => catalogChecked[catalogItemId(doc)])
                          .map((doc) => ({ ...doc, kind: "type" }))
                      )}
                    >
                      <FaTrash />
                    </button>
                  </div>
                  {catalogSelectedTypes.map((doc) => {
                    const id = catalogItemId(doc);
                    return (
                      <div key={doc.slug || doc.code} className="cd-catalog-delete-row">
                        <label className="cd-catalog-check">
                          <input
                            type="checkbox"
                            checked={Boolean(id && catalogChecked[id])}
                            disabled={catalogSaving || catalogDeleting || !id}
                            onChange={(e) => {
                              if (!id) return;
                              setCatalogChecked((prev) => ({ ...prev, [id]: e.target.checked }));
                            }}
                          />
                          <span>{doc.label || doc.name}</span>
                        </label>
                        <button
                          type="button"
                          className="cd-icon-btn danger"
                          title="Delete"
                          aria-label={`Delete ${doc.label || doc.name}`}
                          disabled={catalogSaving || catalogDeleting || !id}
                          onClick={() => catalogDeleteItems([{ ...doc, kind: "type" }])}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    );
                  })}
                  <p className="cd-catalog-delete-hint">Remove every document type before you can delete this category.</p>
                </>
              ) : (
                <>
                  <p className="cd-catalog-delete-hint">
                    {categories.length <= 1
                      ? "At least one main category must remain."
                      : "All document types are removed. You can delete this category."}
                  </p>
                  <div className="cd-catalog-delete-row">
                    <span>{catalogSelectedCat.label || catalogSelectedCat.name}</span>
                    <button
                      type="button"
                      className="cd-icon-btn danger"
                      title="Delete category"
                      aria-label="Delete category"
                      disabled={
                        catalogSaving
                        || catalogDeleting
                        || categories.length <= 1
                        || !catalogItemId(catalogSelectedCat)
                      }
                      onClick={() => catalogDeleteItems([{ ...catalogSelectedCat, kind: "category" }])}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions className="cd-catalog-dialog-actions">
          <Button className="cd-catalog-cancel" onClick={closeCatalogDialog} disabled={catalogSaving}>
            Cancel
          </Button>
          <Button className="cd-catalog-create" onClick={handleCatalogCreate} disabled={catalogSaving}>
            {catalogSaving ? "Saving…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(catalogDelete)}
        TransitionComponent={Transition}
        keepMounted
        onClose={closeCatalogDelete}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const items = catalogDelete?.items || [];
              if (items.length > 1) {
                return `Delete ${items.length} selected document types? This cannot be undone.`;
              }
              const item = items[0];
              if (item?.kind === "category") {
                return `Delete category "${item.name}"? This cannot be undone.`;
              }
              return `Delete document type "${item?.name || ""}"? This cannot be undone.`;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCatalogDelete} disabled={catalogDeleting}>No</Button>
          <Button onClick={handleCatalogDelete} disabled={catalogDeleting}>
            {catalogDeleting ? "Deleting…" : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteRow)}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="cd-delete-dialog"
        onClose={handleDeleteClose}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cd-delete-dialog">
            Are you sure you want to delete this{" "}
            <strong>{deleteRow?.document_name || docLabels[deleteRow?.doc] || "document"}</strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>No</Button>
          <Button onClick={handleDeleteConfirmed}>Yes</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ComplianceDocuments;
